import { ProductContainer } from "../catalogs/catalog.products";
import { DataTypeName, makeBrief } from "../data/data-types";
import { LayoutItemRef } from "../layouts/layouts";
import { ProductRef } from "../products/product";
import { TemplateRef } from "../templates/templates";

export type BoundRect = {
  top: number;
  right: number;
  bottom: number;
  left: number;
  zoom: number;
};

export type DesignItemType =
  | "text"
  | "image"
  | "product"
  | "template"
  | "page"
  | "icon"
  | "bg"
  | "product-image"
  | "product-list";

export type DesignSubtype = "product";

export const imagePositions = ["Fit", "Fill", "Stretch", "Center"] as const;

export type ImagePosition = (typeof imagePositions)[number];

export type CatalogExtension = {
  type: "productlist";
  version?: string;
  fields: string[];
  orderBy?: string;
  orderDesc?: boolean;
  grouping?: string;
  groupOrder?: string;
  HeaderColor?: string;
  HeaderBkgColor?: string;
  HeaderPadding?: string;
  ProductPadding?: string;
  GroupPadding?: string;
  Border?: string;
  BorderColor?: string;
  HeaderFontSize?: string;
  HeaderFont?: string;
  ProductBkgColor?: string;
  AltRowBkgColor?: string;
  ProductColor?: string;
  ProductFontSize?: string;
  ProductFont?: string;
  /** Product cell vertical-align: "middle" | "bottom"; unset = top */
  ProductVAlign?: string;
  VariantRow?: boolean;
  GroupBy?: string;
  UseGroupColor?: boolean;
  GroupColor?: string;
  GroupBkgColor?: string;
  GroupFontSize?: string;
  GroupFont?: string;
  GroupOrder?: string;
  GroupThumb?: boolean;
  GroupDesc?: boolean;
  HideHeader?: boolean;
  /** When true, body was manually customized — design changes will regenerate standard layout */
  custom?: boolean;
} & ProductContainer;

export type ItemStyle = {
  left?: number;
  top?: number;
  width?: number;
  height?: number;
  zIndex?: number;
  fontSize?: string;
  color?: string;
  backgroundColor?: string;
  transform?: string;
};

export type DesignItem = {
  w: number;
  h: number;
  x: number;
  y: number;
  z?: number;
  r?: number;

  Color?: string;
  ColorSrc?: string;
  BorderColor?: string;
  BorderWidth?: number;
  BorderRadius?: number;
  BorderStyle?: string;
  Prime?: boolean;

  exp?: boolean;
  fix?: boolean;
  scale?: boolean;
  subtype?: DesignSubtype;
};

export type DesignItemInfo = {
  product?: ProductRef;
  template?: TemplateRef;
  content?: string;
  src?: string;
  /** Higher-resolution, size-aware image URL for the editable canvas only.
   * Page-rail / clipboard previews keep the lighter `src` (150 thumb). */
  srcHi?: string;
  path?: string;
  title?: string;
  extension?: CatalogExtension;
  templateItems?: TemplateItemRef[];
  imageField?: string;
};

export type DesignItemMeta = "template" | "catalog" | "layout";

export type DesignItemRef = {
  id: string;
  type?: DesignItemType;
  content?: string;
  imgPosition?: ImagePosition;
  version: number;
  di: DesignItem;
  style?: ItemStyle;
  info?: DesignItemInfo;
  meta?: DesignItemMeta;
};

export type TemplateItem = DesignItem & {
  type: DesignItemType;
  text?: string;
  src?: string;
  imgMode?: "field" | "image";
  position?: ImagePosition;
};

export type TemplateItemRef = {
  di: TemplateItem;
  meta: "template";
} & DesignItemRef;

export const orderDesignItem = <T extends DesignItemRef>(
  items: T[],
  item: T,
  isFront: boolean,
  withStyle: boolean
) => {
  if (!item) return items;

  items.sort((a, b) => (a.di.z < b.di.z ? -1 : 1));
  let z = isFront ? 1 : 2;
  items
    .filter((i) => i !== item)
    .forEach((i) => {
      i.di.z = z++;
      if (withStyle) i.style = { ...i.style, zIndex: i.di.z };
    });
  item.di.z = isFront ? z : 1;
  if (withStyle) item.style = { ...item.style, zIndex: isFront ? z : 1 };
  return items;
};

export const removeDesignItem = <T extends DesignItemRef>(
  items: T[],
  item: T
) => {
  if (!item) return;
  return items.filter((i) => i.id !== item.id);
};

export const getItemDescription = (item: DesignItemRef, short: boolean) => {
  if (!item.type) return "empty";

  if (item.info.extension) {
    return short
      ? ""
      : item.info.extension.type === "productlist"
      ? "Product List"
      : "Extension";
  }

  const p = item.meta === "catalog" ? item.info.product : null;

  const product = p ? " P: <b>" + p.product.name + "</b> " : "";

  if (item.info.template) {
    return (
      product +
      (item.info.template
        ? " Card: " +
          (item.info.template.template.name || (item as LayoutItemRef).di.text)
        : "")
    );
  }

  if (item.meta === "template") {
    const ti = item as TemplateItemRef;
    if (ti.type === "image")
      return ti.di.imgMode === "field" && !short
        ? "Field: " + ti.di.src
        : ti.info.path ?? ti.type;
  }

  if (item.type === "image" && !p) {
    return item.info.title ?? item.type;
  }

  switch (item.type) {
    case "bg":
      return short ? "" : "rectangle";
    case "text":
      return product + (short ? "text" : makeBrief(item.content, 100));
    case "template":
      return "<b>Card</b> " + ((item as LayoutItemRef).di.text || "");
    default:
      return product + (short ? "" : item.info.title ?? item.type);
  }
};

export const VariantPrefix = "Variant";

export type ImageFieldParams = {
  name: string;
  pos: ImagePosition;
  w: number;
  h: number;
  field?: string;
};

export const parseImageField = (expr: string): ImageFieldParams => {
  return parseImageFieldArgs(expr.split(":"), expr);
};

export const parseImageFieldArgs = (
  fieldArgs: string[],
  field?: string
): ImageFieldParams => {
  let prefix = "";
  if (fieldArgs[0] === VariantPrefix) {
    prefix = VariantPrefix + ":";
    fieldArgs.shift();
  }
  const result = {
    name: prefix + fieldArgs[0],
    pos: "Fit" as ImagePosition,
    w: 0,
    h: 0,
    field,
  };
  let ph = 3;
  if (fieldArgs.length > 1) {
    if (!isNaN(+fieldArgs[1])) {
      result.w = +fieldArgs[1];
      ph = 2;
    } else {
      result.pos = fieldArgs[1] as ImagePosition;
      result.w = fieldArgs.length > 2 ? +fieldArgs[2] : 0;
    }
    if (fieldArgs.length > ph && !isNaN(+fieldArgs[ph])) {
      result.h = +fieldArgs[ph];
    }
  }
  return result;
};

export type TextFieldParams = {
  name: string;
  /** ":plain" — render the field's text without its own markup. */
  plain: boolean;
  /** Truncation length; 0 means the field is not truncated. */
  len: number;
  field?: string;
};

/**
 * Round-trip the argument forms a Text / Rich Text field accepts —
 * `Description`, `Description:plain`, `Description:plain:200`,
 * `Description:200` — the same set `TemplateField.GetTextTag` reads server-side.
 *
 * Pass `dataType` to normalize rather than round-trip: on a Rich Text field a
 * bare length already strips server-side, so `Description:200` IS plain and is
 * reported as such. Without it the editor would show an off switch next to
 * output that is plainly stripped.
 */
export const parseTextField = (
  expr: string,
  dataType?: string
): TextFieldParams => {
  const fieldArgs = expr.split(":");
  let prefix = "";
  if (fieldArgs[0] === VariantPrefix) {
    prefix = VariantPrefix + ":";
    fieldArgs.shift();
  }
  let plain = fieldArgs.length > 1 && fieldArgs[1].toLowerCase() === "plain";
  const lenArg = plain ? fieldArgs[2] : fieldArgs[1];
  const len = lenArg && !isNaN(+lenArg) ? +lenArg : 0;
  if (len && dataType === DataTypeName.RichText) plain = true;
  return { name: prefix + fieldArgs[0], plain, len, field: expr };
};

export const formatTextField = (p: TextFieldParams) =>
  p.name + (p.plain ? ":plain" : "") + (p.len > 0 ? ":" + p.len : "");

export const getPositonAttr = (meta: DesignItemMeta) =>
  meta === "catalog" ? "Position" : "z";

export const setRotationStyle = (item: DesignItemRef) => {
  if (item.di.r)
    item.style = { ...item.style, transform: `rotate(${item.di.r}deg)` };
};
